import type {
  LinkedInPrivacySettings,
  MetaMediaType,
  PostState,
  PrivacySettings,
  SocialMediaAccountId,
  TikTokPrivacySettings,
  YouTubePrivacySettings,
  GoogleBusinessProfilePostType,
  GoogleBusinessProfileCtaType,
  PlatformMediaType,
} from '@Components/social-media/post.vo';
import type {LoadingStates} from '@Utils/loading.util';
import type {InstagramAudioTrack} from '@Components/social-media-wizard/social-media-wizard.types';
import type {DesignSelection} from '@Components/social-media/social-media.types';
import type {MetricsAvailability} from '@Components/social-media/social-platform-metrics.types';

export type PosterHashedId = string;
export type PageHashedId = string;

export enum SocialPostPublishingAction {
  PUBLISH = 'PUBLISH',
  SCHEDULE = 'SCHEDULE',
  SAVE = 'SAVE',
}

export enum RetryStatus {
  NOT_RETRYING = 'NOT_RETRYING',
  RETRYING = 'RETRYING',
}

export interface SocialPostCover {
  timestamp: number | null;
  coverUrl: string;
}

export type SocialMediaPublishingParams =
  | InstagramPublishingParams
  | TwitterPublishingParams
  | FacebookPublishingParams
  | TikTokPublishingParams
  | YouTubePublishingParams
  | LinkedInPublishingParams
  | PinterestPublishingParams
  | GoogleBusinessProfilePublishingParams
  | ThreadsPublishingParams;

/**
 * Per-account metrics health. All fields are optional because legacy `publishing_params`
 * blobs predate them — readers default `metricsAvailability` to `AVAILABLE` and treat
 * a null/absent `metricsWindowHours` as "no expiry" (lifetime insights). When set
 * (e.g. 24 for IG/FB Stories), the frontend pairs it with `post.publishOn` to switch
 * from "Update now" to a "Final metrics" banner once the window closes.
 */
export interface MetricsHealthFields {
  metricsAvailability?: MetricsAvailability;
  consecutiveMetricsFailureCount?: number;
  metricsWindowHours?: number | null;
}

export interface LinkedInPublishingParams extends MetricsHealthFields {
  caption: string;
  idSocialMediaAccount: number;
  privacySettings: LinkedInPrivacySettings;
  title: string;
  publishedURL: null | string;
  publishErrorMessage: null | string;
  publishId: null | string;
  state: PostState;
  graphicsMetaMap: GraphicsMetaMapType;
  isOriginalDeleted: boolean;
}

export type GraphicsMetaMapType = Record<PosterHashedId, PageGraphicsMetaMap>;
export type PageGraphicsMetaMap = Record<PageHashedId, GraphicMeta>;

export interface PinterestPublishingParams extends MetricsHealthFields {
  caption: string;
  idSocialMediaAccount: number;
  privacySettings: PrivacySettings;
  title: string;
  publishedURL: null | string;
  publishErrorMessage: null | string;
  publishId: null | string;
  state: PostState;
  boardId: null | string;
  websiteLink: null | string;
  isOriginalDeleted: boolean;
  graphicsMetaMap: GraphicsMetaMapType;
}

export interface TwitterPublishingParams extends MetricsHealthFields {
  caption: string;
  idSocialMediaAccount: number;
  publishedURL: null | string;
  publishErrorMessage: null | string;
  publishId: null | string;
  state: PostState;
  isOriginalDeleted: boolean;
  graphicsMetaMap: GraphicsMetaMapType;
}

export interface FacebookPublishingParams extends MetricsHealthFields {
  caption: string;
  title: string;
  idSocialMediaAccount: number;
  publishedURL: null | string;
  publishErrorMessage: null | string;
  publishId: null | string;
  state: PostState;
  isOriginalDeleted: boolean;
  graphicsMetaMap: GraphicsMetaMapType;
}

export interface InstagramReelMusic extends InstagramAudioTrack {
  audioVolume?: number;
  videoVolume?: number;
}

export interface InstagramPublishingParams extends MetricsHealthFields {
  caption: string;
  idSocialMediaAccount: number;
  publishedURL: null | string;
  publishErrorMessage: null | string;
  publishId: null | string;
  state: PostState;
  isOriginalDeleted: boolean;
  graphicsMetaMap: GraphicsMetaMapType;
  reelMusic: InstagramReelMusic | null;
}

export interface TikTokPublishingParams extends MetricsHealthFields {
  caption: string;
  title: string;
  disableDuet: boolean;
  disableComment: boolean;
  disableStitch: boolean;
  isSelfBrand: boolean;
  isPromotingThirdParty: boolean;
  idSocialMediaAccount: number;
  privacySettings: TikTokPrivacySettings;
  publishedURL: null | string;
  publishErrorMessage: null | string;
  publishId: null | string;
  state: PostState;
  isOriginalDeleted: boolean;
  graphicsMetaMap: GraphicsMetaMapType;
}

export interface YouTubePublishingParams extends MetricsHealthFields {
  caption: string;
  title: string;
  privacySettings: YouTubePrivacySettings;
  idSocialMediaAccount: number;
  publishedURL: null | string;
  publishErrorMessage: null | string;
  publishId: null | string;
  state: PostState;
  isOriginalDeleted: boolean;
  graphicsMetaMap: GraphicsMetaMapType;
}

export interface GoogleBusinessProfilePublishingParams extends MetricsHealthFields {
  caption: string;
  postType: GoogleBusinessProfilePostType;
  ctaType: null | GoogleBusinessProfileCtaType;
  ctaURL: string;
  eventStartTime: null | number;
  eventEndTime: null | number;
  idSocialMediaAccount: number;
  publishedURL: null | string;
  publishErrorMessage: null | string;
  publishId: null | string;
  state: PostState;
  isOriginalDeleted: boolean;
  graphicsMetaMap: GraphicsMetaMapType;
}

export interface ThreadsPublishingParams extends MetricsHealthFields {
  caption: string;
  idSocialMediaAccount: number;
  publishedURL: null | string;
  publishErrorMessage: null | string;
  publishId: null | string;
  state: PostState;
  isOriginalDeleted: boolean;
  graphicsMetaMap: GraphicsMetaMapType;
}

export interface GraphicMeta {
  isVideo: boolean;
  graphicURL: string;
  name: string;
  order: number;
  mediaType?: MetaMediaType;
  cover?: SocialPostCover;
}

export interface PublishingParamsInsertionData {
  publishingParams: SocialMediaPublishingParams;
  idSocialMediaAccount: SocialMediaAccountId;
}

export interface PublishingParamsCaptionPayload {
  caption: string;
  idSocialMediaAccount: SocialMediaAccountId;
  isMultiCharacterChange: boolean;
}

export interface PublishingParamsCommonCaptionPayload {
  caption: string;
  isMultiCharacterChange: boolean;
}

export type PublishingParamsCommonTextPayload = Record<
  number,
  {
    title: string;
    caption: string;
    areURLsAllowed?: boolean;
  }
>;

export interface PublishingParamsIsPromotingThirdParty {
  isPromotingThirdParty: boolean;
  idSocialMediaAccount: SocialMediaAccountId;
}

export interface PublishingParamsIsSelfBrand {
  isSelfBrand: boolean;
  idSocialMediaAccount: SocialMediaAccountId;
}

export interface PublishingParamsDisableComment {
  disableComment: boolean;
  idSocialMediaAccount: SocialMediaAccountId;
}

export interface PublishingParamsDisableStitch {
  disableStitch: boolean;
  idSocialMediaAccount: SocialMediaAccountId;
}

export interface PublishingParamsDisableDuet {
  disableDuet: boolean;
  idSocialMediaAccount: SocialMediaAccountId;
}

export interface PublishingParamsMediaTypePayload {
  mediaType: MetaMediaType;
  idSocialMediaAccount: SocialMediaAccountId;
}

export interface PublishingParamsTitlePayload {
  title: string;
  idSocialMediaAccount: SocialMediaAccountId;
  areURLsAllowed: boolean;
}

export interface PublishingParamsTextUpdatePayload {
  title: string;
  caption: string;
  idSocialMediaAccount: SocialMediaAccountId;
  areURLsAllowed: boolean;
}

export interface PublishingParamsLinkedInPrivacySettingsPayload {
  privacySettings: LinkedInPrivacySettings;
  idSocialMediaAccount: SocialMediaAccountId;
}

export interface PublishingParamsYouTubePrivacySettingsPayload {
  privacySettings: YouTubePrivacySettings;
  idSocialMediaAccount: SocialMediaAccountId;
}

export interface PublishingParamsTikTokPrivacySettingsPayload {
  privacySettings: TikTokPrivacySettings;
  idSocialMediaAccount: SocialMediaAccountId;
}

export interface PublishingParamsGoogleBusinessProfileCtaTypePayload {
  ctaType: GoogleBusinessProfileCtaType | null;
  idSocialMediaAccount: SocialMediaAccountId;
}

export interface PublishingParamsGoogleBusinessProfilePostTypePayload {
  postType: GoogleBusinessProfilePostType;
  idSocialMediaAccount: SocialMediaAccountId;
}

export interface PublishingStatusForAccountPayload {
  publishingStatus: LoadingStates;
  idSocialMediaAccount: SocialMediaAccountId;
  platformMediaType: PlatformMediaType;
}

export interface PublishingParamsPublishErrorMessagePayload {
  publishErrorMessage: string | null;
  idSocialMediaAccount: SocialMediaAccountId;
  platformMediaType: PlatformMediaType;
}

export interface PublishingParamsWebsiteLinkPayload {
  websiteLink: string | null;
  idSocialMediaAccount: SocialMediaAccountId;
}

export interface PublishingParamsReelMusicPayload {
  reelMusic: InstagramReelMusic | null;
  idSocialMediaAccount: SocialMediaAccountId;
}

export interface PublishingParamsBoardIdPayload {
  boardId: string | null;
  idSocialMediaAccount: SocialMediaAccountId;
}

export interface PublishingParamsCommonPostCoverPayload {
  coverObject: SocialPostCover;
  design: DesignSelection;
}

export interface PublishingParamsSocialPostCoverPayload extends PublishingParamsCommonPostCoverPayload {
  idAccount: SocialMediaAccountId;
  platformMediaType: PlatformMediaType;
}

export enum PublishingMediaType {
  IMAGE = 'IMAGE',
  VIDEO = 'VIDEO',
}

export enum MultiPostDesignActionPurposes {
  EDIT = 'EDIT',
  RESIZE = 'RESIZE',
}

export interface DesignMeta {
  isVideo: boolean;
  designName: string;
  previewUrl: string;
}
